/**
 * SHARED GATEWAY VARIANTS — API Gateway responses, not handler responses.
 *
 * Extracted after pilot 2 (dispute) per EXECUTABLE_LEGACY_CONTRACTS.md §6, from the
 * refund (bucket A) and dispute (bucket C) pilots, which had independently derived the
 * same set. Sixteen HTTP repos remain; each of them re-deriving API Gateway's behaviour
 * is sixteen chances to get it wrong, and one of those chances has already been taken —
 * see the 503 note below.
 *
 * WHAT THIS FILE IS. The responses API Gateway itself produces, before or instead of
 * your handler. They are the same for every HTTP API v2 in the fleet, so they belong
 * here rather than in each contract. Everything a HANDLER returns stays in that repo's
 * own `schemas.ts`.
 *
 * HOW TO USE IT. Import the primitives and `GATEWAY_ALL`, then REDUCE to what your API
 * can actually emit, and say why in a comment:
 *
 *   import { LambdaProxyResponse, variant, gatewayMessage, GATEWAY_ALL } from '../_shared/gateway';
 *
 *   // This API has no authorizer (every route AuthorizationType: NONE), so 401/403
 *   // are unreachable and are deliberately excluded rather than carried dead.
 *   export const GATEWAY_VARIANTS = {
 *     GATEWAY_INTERNAL:    GATEWAY_ALL.GATEWAY_INTERNAL,
 *     GATEWAY_UNAVAILABLE: GATEWAY_ALL.GATEWAY_UNAVAILABLE,
 *   } as const;
 *
 * Excluding an unreachable variant is not tidying — it is what lets you use it as a
 * NEGATIVE fixture. A 401 arriving from a route that has no authorizer means the
 * deployed auth posture changed, and the contract should go RED rather than quietly
 * accept it.
 *
 * WHAT IS *NOT* SHARED, and must not be moved here:
 *   - CORS header triples. They differ per repo in ways a recorder must not smooth
 *     over: dispute emits capital-C `Content-Type`, refund lowercase `content-type`,
 *     and within dispute one 200 carries no headers at all. Pin them per repo.
 *   - REST API v1 gateway bodies. `donation-elements`'s `dualAuthRestV1` has a
 *     DIFFERENT set — 504 rather than 503, and a different `{message}` universe. When
 *     that repo is contracted, add a sibling `gatewayRestV1.ts`; do not widen this file.
 *
 * PROVENANCE. Body strings are AWS's documented defaults for HTTP APIs, which have no
 * configurable gateway responses. Access logs record status but never bodies, so the
 * BODIES are DOCUMENTATION-DERIVED while the STATUSES are observed. Every contract
 * importing this file must carry that as a residual.
 */
import { z } from 'zod';

/**
 * What the Lambda proxy (payload format 2.0) returns, and what the recorder captures.
 * NOTE the capture point: this is the Lambda's return value, NOT the wire. The gateway
 * adds its own CORS headers afterwards, so a recorder capturing at the HTTP edge sees
 * a different header set than these schemas pin. Decide the capture layer once,
 * fleet-wide, and state it in every CLOSURE.
 */
export const LambdaProxyResponse = z
  .object({
    statusCode: z.number().int(),
    headers: z.record(z.string()).optional(),
    body: z.string(),
  })
  .strict();
export type LambdaProxyResponse = z.infer<typeof LambdaProxyResponse>;

/** Gateway-generated responses: the header set is API Gateway's, so it is not pinned. */
export const ANY_HEADERS = z.record(z.string()).optional();

/** A handler that sets no headers at all. Distinct from ANY_HEADERS and load-bearing. */
export const NO_HEADERS = z.undefined();

export const variant = <S extends number, H extends z.ZodTypeAny, B extends z.ZodTypeAny>(
  statusCode: S,
  headers: H,
  body: B,
) => z.object({ statusCode: z.literal(statusCode), headers, body }).strict();

export const gatewayMessage = <M extends string>(m: M) =>
  z.object({ message: z.literal(m) }).strict();

/**
 * The full HTTP API v2 gateway set. Reduce it per repo; do not extend it here without
 * evidence from a deployed API.
 */
export const GATEWAY_ALL = {
  /** `$request.header.Authorization` identity source missing or invalid → the authorizer is never invoked. */
  GATEWAY_UNAUTHORIZED: variant(401, ANY_HEADERS, gatewayMessage('Unauthorized')),
  /** Authorizer returned `isAuthorized: false` (SIMPLE response; cache key is the header). */
  GATEWAY_FORBIDDEN: variant(403, ANY_HEADERS, gatewayMessage('Forbidden')),
  /** Any unhandled handler exception, a malformed envelope, or an authorizer error. */
  GATEWAY_INTERNAL: variant(500, ANY_HEADERS, gatewayMessage('Internal Server Error')),
  /**
   * The route matched nothing. **This one is invisible to the access log**, which is
   * why nearly every contract in this lane missed it.
   *
   * MEASURED 2026-08-29 on api `9wyp5abvt4`: `GET /nope` and `PUT /api-keys` both
   * returned `404 {"message":"Not Found"}` unauthenticated, and a Logs Insights count
   * over the following fifteen minutes showed the `401` probe and the `OPTIONS` 204s
   * but NEITHER 404. HTTP API v2 matches the route BEFORE invoking the authorizer or
   * the Lambda and emits no access-log entry when nothing matches.
   *
   * So on any API without a `$default` route, a typo'd path or an undeclared method
   * yields this — trivially, with no credentials — and leaves no trace in the evidence
   * source the closure argument relies on. **Its lifetime count is UNKNOWABLE, not
   * zero.** "Zero 404s in N records" is a true statement about a blind instrument.
   *
   * Nuance: `OPTIONS` on an unmatched path returns 204, not 404, because CORS preflight
   * is answered for any path; the OPTIONS arm is reachable only via non-preflight
   * OPTIONS.
   *
   * Omit this variant ONLY if the API declares a `$default` route — check with
   * `aws apigatewayv2 get-routes` and say which it was in CLOSURE.
   */
  GATEWAY_NOT_FOUND: variant(404, ANY_HEADERS, gatewayMessage('Not Found')),
  /**
   * NAMED FOR THE WIRE, NOT FOR A CAUSE — the naming matters, see below.
   *
   * **503, not 504.** 504 `Gateway Timeout` is REST API v1. An adversarial critic
   * asserted 504 for HTTP API v2 on 2026-08-29 and was wrong; accepting it would have
   * corrupted the pin in refund and in all 16 HTTP repos. MEASURED on the refund API's
   * lifetime access log: a genuine integration timeout — `integrationLatency > 29000`,
   * `integrationStatus = "-"` (the Lambda was never reached) and
   * `integrationServiceStatus = 200` (ruling out a throttle) — was recorded by AWS as
   * status **503**.
   *
   * One wire shape, TWO causes, opposite effects. See `GATEWAY_UNAVAILABLE_CAUSES`.
   */
  GATEWAY_UNAVAILABLE: variant(503, ANY_HEADERS, gatewayMessage('Service Unavailable')),
} as const;

/**
 * The two causes behind the single 503, and how to tell them apart after the fact.
 * They are indistinguishable in the response and separated only by
 * `integrationServiceStatus` in the access log — which is why the variant is named for
 * the wire. Naming it `GATEWAY_TIMEOUT`, as the refund pilot did, asserts the cause
 * that has never actually been observed.
 *
 * Per-repo counts belong in that repo's CLOSURE, not here; what is fleet-wide is the
 * distinction and its consequence.
 */
export const GATEWAY_UNAVAILABLE_CAUSES = {
  /**
   * Lambda concurrency throttling. `integrationServiceStatus: 429`, single-digit-ms
   * latency. The function NEVER RAN, so nothing was written — the 503 is honest.
   * This is 100% of what has been observed anywhere in the fleet so far.
   */
  THROTTLED: { integrationServiceStatus: 429, wroteAnything: false },
  /**
   * Integration timeout. Reachable wherever `TimeoutInMillis` (30,000 by default) is
   * below the Lambda's own `Timeout` — commonly 180 s in this fleet, a 150 s gap. The
   * handler KEEPS RUNNING AND KEEPS WRITING after the caller has been told the request
   * failed. THE DANGEROUS CASE: a 503 here does not mean nothing happened.
   *
   * Check `TimeoutInMillis` against the function timeout per repo (`get-integrations`
   * vs `lambda list-functions`) and record the gap in CLOSURE.
   */
  INTEGRATION_TIMEOUT: { integrationServiceStatus: 200, wroteAnything: 'maybe' },
} as const;

export type GatewayVariantName = keyof typeof GATEWAY_ALL;
